import React, { useCallback, useMemo, useState, useEffect } from "react"
import { Ship } from "../../App/Ship"
import { GetApp } from "../../App"
import { GetDay, GetLog, GetShip } from "../../App/Context"
import { Time } from "../../App/Time"
import { useEditState } from "../../App/useEditState"
import { value } from "../../Components"
import { MultiSelect, MultiSelectValue } from "../../Components/MultiSelect"
import { AutocompleteInput, NumberInput } from "../../Components/Input"
import { Block, BlockField, BlockFieldInputs, BlockFieldInputsDivider, BlockRow } from "../../Components/Block"
import { EditGroup, saveErrorMessage } from "../../Components/EditGroup"
import { Icon, Icons } from "../../Components/Icon"
import { IAppState, IEditPermissivenessLevel, ILog, ILogEditableField, ILogResponse, IShip, IShipDataSail, IStatus, IStatusType, IShipStatus, IShipStatusData, IShipAutocompleteResponse, Status, ApiClient, LogApi, ShipApi } from "../../Types"
import { logFieldEditable } from "./editability"
import { coveringStatus, engineIdleAtLog, engineRunningAtLog, visibleSailStatuses } from "./status"
import { logger } from "../../App/Logger"

// Derived engine on/off + identity for a single log entry, shared by the interactive
// LogNavigationView editor and Print/LogView so both agree on which engines are running.
export interface LogEngineInfo {
  id: string;
  number: number | undefined;
  nmeaNumber: number | undefined;
  name: string | undefined;
  on: boolean;
  // Running below the ship's idle RPM threshold (#619). Rendered as the same
  // dot-in-ring the log rows use, so the Navigation field can't claim an idling
  // engine is under load (#1819 testing).
  idle: boolean;
}

// Points of sail, ordered from closest to the wind to furthest, matching the fixed
// true-wind-angle buckets requested in #68.
export type PointOfSail = "noGo" | "closeHauled" | "beamReach" | "broadReach" | "running";
export type WindSide = "port" | "starboard";

export interface CourseToWind {
  angle: number;
  pointOfSail: PointOfSail;
  side?: WindSide;
}

export const LogNavigationViewData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  saveLog(api: ApiClient, shipId: number, logId: number, values: any): Promise<ILogResponse> {
    return new LogApi(api).logs.update({ params: { shipId, logId }, body: values }).call();
  },
  loadLogCommentAutocomplete(api: ApiClient, shipId: number): Promise<IShipAutocompleteResponse> {
    return new ShipApi(api).ships.autocomplete({ params: { shipId, field: "logComment" } }).call();
  },
  updateEngine(api: ApiClient, shipId: number, logId: number, engine: string, on: boolean) {
    return new LogApi(api).logs.updateEngine({ params: { shipId, logId }, body: { engine, on } }).call();
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  updateSails(api: ApiClient, shipId: number, logId: number, values: any) {
    return new LogApi(api).logs.updateSails({ params: { shipId, logId }, body: { sails: values } }).call();
  },

  // --- Shared log-entry derivations (canonical: the interactive Log view) ---------------
  // Print/LogView references these so the two views cannot drift. The genuinely
  // view-specific bits (sailStatuses point-in-time window, the sailStatusValues editor
  // shape) stay in their own components.

  engineStatuses(statuses: IStatus[] | undefined): IStatus[] {
    return (statuses || []).filter((s) => s.statusType == IStatusType.engine);
  },

  // Defensively parse the ship-status blob; a malformed status.data is swallowed rather
  // than thrown so a single bad row never blanks the whole view.
  statusData(status: IStatus | undefined): IShipStatusData | undefined {
    if (status && status.data) {
      try {
        const parsed = Status.parse(status);
        if (parsed.statusType === IStatusType.ship) {
          return parsed.dataParsed;
        }
      } catch (e) {
        logger.error("Failed to parse status.data", e);
      }
    }
    return undefined;
  },

  sailName(app: IAppState, id: string, reef: number, sails: IShipDataSail[]): string {
    const t = app.getT("ShipDataView_sail");
    const sail = sails.find((s) => s.id == id);
    if (sail && sail.sail) {
      const name = t(sail.sail) || sail.sail;
      if (reef < 1) {
        return name;
      }
      return t("reef", { sail: name, reef: t(`reef_${reef}`) });
    }
    return "";
  },

  // Canonical engine on/off rule (the Log view's): an engine is running if the engine
  // status covering this log entry is open OR its logged rpm is at or above the ship's
  // engine-off threshold -- shared with `LogView`/`Print/LogListView` through
  // `engineRunningAtLog`. The covering check is applied here rather than left to the
  // caller (#506) -- `Print/DaysView` hands over the day's full status list, where a
  // status opened *after* this log used to match.
  engines(ship: IShip, log: ILog, engineStatuses: IStatus[]): LogEngineInfo[] {
    const engineInfo: LogEngineInfo[] = [];
    const thresholds = Ship.shipSettings(ship).thresholds;
    for (const e of Ship.engines(ship)) {
      const status = coveringStatus(engineStatuses, log.id, e.id);
      if (e.number == undefined && e.nmeaNumber == undefined) {
        continue;
      }
      const n = value(-1, e.nmeaNumber, e.number);
      const on = engineRunningAtLog(log.engineRpms, n, status, thresholds);
      engineInfo.push({
        id: e.id,
        number: e.number,
        nmeaNumber: e.nmeaNumber != undefined ? e.nmeaNumber : e.number,
        name: e.engineShortName,
        on: on,
        idle: on && engineIdleAtLog(log.engineRpms, n, status, thresholds),
      });
    }
    return engineInfo;
  },

  // Display name for one engine in a label: its short name, else its NMEA number,
  // else its configured number. Never "#1"-style -- the number is only ever shown
  // next to the translated "Engine" word, which already says what it numbers.
  engineLabelName(engine: LogEngineInfo): string {
    return engine.name || `${engine.nmeaNumber != undefined ? engine.nmeaNumber : engine.number}`;
  },

  // Label for the navigation view's engine field.
  //
  // Two or more engines share a single "Engine {name}" so the word is not repeated
  // per engine in a row that is already tight ("Engine Starboard | Portside", not
  // "Engine Starboard | Engine Portside").
  //
  // A single *named* engine keeps "Engine {name}", but a single *unnamed* one drops
  // to the bare "Engine": there is nothing to disambiguate, so "Engine 1" would only
  // add noise. No engines at all yields an empty label rather than a stray word.
  engineLabel(app: IAppState, engines: LogEngineInfo[]): string {
    const ft = app.getT("LogNavigationView");
    if (engines.length === 0) {
      return "";
    }
    if (engines.length === 1) {
      return engines[0].name ? ft("engine_name", { name: engines[0].name }) : ft("engine");
    }
    return ft("engine_name", { name: engines.map(LogNavigationViewData.engineLabelName).join(" | ") });
  },

  // Width budget for the engine field, so a one-engine ship does not reserve the
  // room a twin needs. Sized off the ship's configured `engineCount` rather than the
  // rendered engine list, so the field keeps its place while engine data is still
  // loading (or when a configured engine is skipped for having no number at all).
  engineFieldMaxWidth(engineCount: number): string {
    return engineCount === 1 ? "8.6em" : `${engineCount * 9}em`;
  },

  // True wind angle (0-180, symmetric either side of the bow) + point-of-sail category
  // + wind side, derived from heading and true windDirection (both plain ILog fields,
  // no NMEA/live data required). See #68. `undefined` inputs (0 is a valid heading or
  // wind direction, not "missing") yield `undefined`, since there's nothing to derive.
  courseToWind(heading: number | undefined, windDirection: number | undefined): CourseToWind | undefined {
    if (heading == undefined || windDirection == undefined) {
      return undefined;
    }
    // Signed shortest turn from heading to wind; positive is to starboard, negative to
    // port (same convention as the course-change side calculation in Log/nmea.ts).
    const signed = ((windDirection - heading + 540) % 360) - 180;
    const angle = Math.abs(signed);
    let pointOfSail: PointOfSail;
    if (angle < 40) {
      pointOfSail = "noGo";
    } else if (angle < 70) {
      pointOfSail = "closeHauled";
    } else if (angle < 120) {
      pointOfSail = "beamReach";
    } else if (angle < 150) {
      pointOfSail = "broadReach";
    } else {
      pointOfSail = "running";
    }
    // Dead ahead (0) or dead astern (180): neither side is meaningful.
    const side: WindSide | undefined = (signed == 0 || angle == 180) ? undefined : (signed > 0 ? "starboard" : "port");
    return { angle, pointOfSail, side };
  },
};

interface LogNavigationViewProps {
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  onClick?: any;
  status?: IStatus;
  statuses?: IStatus[];
  updated?: string;
  // Issue #1136: reports the save response's authoritative log back to `LogView`, so
  // its edit history resolves a just-saved field's "new value" from it rather than
  // from the (still pre-edit) `log` the day's `logs` list holds until the next live
  // broadcast / full day load. Optional: harnesses rendering this view standalone
  // have no edit history to keep in sync.
  recordSavedLog?: (log: ILog) => void;
  // Seeds the save-error state (issue #529) — for catalog/demo fixtures only;
  // production always starts with no error, only ever setting one via a real
  // rejected `onSave`.
  initSaveError?: unknown;
}

export function LogNavigationView({ editable, initSaveError, onClick, recordSavedLog, status, statuses, updated: _updated }: LogNavigationViewProps) {
  const app = GetApp()!;
  const [ship, log] = [GetShip(), GetLog()];
  const { logs, auditLogEdits, recordAuditLogEdits, updateLog, updateLogEngine, updateLogSails } = GetDay();
  const ft = app.getT("LogNavigationView");
  const [autocompleteLogComment, setAutocompleteLogComment] = useState<string[] | undefined>(undefined);
  const [edited, onEdited, resetEdited] = useEditState();
  const editableFields = [
    "comment",
    { name: "course", type: "float" },
    { name: "depth", type: "float" },
    { name: "distance", type: "float" },
    { array: true, default: -1, name: "engineRpms", type: "int" },
    { name: "heading", type: "int" },
    "location",
    { name: "log", type: "float" },
    "sails",
    { name: "sog", type: "float" },
    { name: "stw", type: "float" },
  ];
  const formEditable = app.editable && editable;
  const _enginesEditable = formEditable;

  // Mirrors the server's `logs.update` field-editability rule (issue #529) — see
  // `Views/Log/editability.ts`. Replaces the old purely-live-NMEA-status check, which
  // didn't account for the ship's `editPermissiveness` level, the editable-age/recency
  // window, or a field already having been manually superseded on this log.
  const level = Ship.shipSettings(ship).editPermissiveness ?? IEditPermissivenessLevel.strict;
  const fieldEditable = useCallback((field: ILogEditableField) => {
    return formEditable && logFieldEditable(field, log, logs, auditLogEdits, level, Time.timeUtc(), app.forceLogEditable);
  }, [formEditable, log, logs, auditLogEdits, level, app.forceLogEditable]);

  // Issue #1142 (broader fix): mirrors every field's own `readOnly` condition below,
  // so `EditGroup` never even attempts to submit a value for a currently
  // non-editable field -- not even its own (possibly stale, display-rounded)
  // `defaultValue`. This is what stops an unrelated edit (e.g. Comment) from ever
  // resubmitting a protected NMEA-capable field's rounded display value and
  // manufacturing a spurious "changed" edit server-side.
  const isFieldEditable = useCallback((field: string) => {
    switch (field) {
      case ILogEditableField.course:
      case ILogEditableField.depth:
      case ILogEditableField.distance:
      case ILogEditableField.engineRpms:
      case ILogEditableField.heading:
      case ILogEditableField.log:
      case ILogEditableField.sog:
      case ILogEditableField.stw:
        return fieldEditable(field as ILogEditableField);
      case "location":
        // `location` is `.always` server-side, so `fieldEditable()` alone would
        // treat it as editable whenever the form is -- but the input below further
        // restricts it to only the log that actually started the current status.
        return formEditable && status?.startLogId == log.id;
      default:
        // comment, sails: `.always`-editable, gated only by `formEditable` (see
        // their own `readOnly` conditions below).
        return formEditable;
    }
  }, [fieldEditable, formEditable, status, log]);

  const [saveError, setSaveError] = useState<unknown>(initSaveError);
  // Field-name -> localized-label map for `saveErrorMessage`. `course`/`sog`/`stw`/
  // `depth`/`heading`/`log`/`engineRpms` are `.nmeaCapable` and can genuinely appear
  // in an `EditNotAllowedError` (issue #1142: course/sog are GPS-derived, tagged the
  // same as NMEA-supplied fields). `distance` is `.locked` (permanently read-only
  // client-side, so a rejection for it should never actually happen) and
  // `comment`/`location` are `.always` -- all three are mapped anyway since their
  // labels already exist and cost nothing extra; `sails` has no field label of its
  // own (falls back to the raw field name if it were ever rejected, which it never
  // is -- sail changes go through a separate route).
  const saveErrorFieldLabels = useMemo(() => ({
    comment: ft("comment"),
    course: ft("course"),
    depth: ft("depth"),
    distance: ft("distance"),
    engineRpms: ft("engine_rpms"),
    heading: ft("heading"),
    location: ft("location"),
    log: ft("log"),
    sog: ft("sog"),
    stw: ft("stw"),
  }), [ft]);
  const saveErrorText = useMemo(() => saveErrorMessage(saveError, saveErrorFieldLabels, {
    fields: (labels) => ft("save_error_fields", { fields: labels }),
    generic: ft("save_error_generic"),
  }), [saveError, saveErrorFieldLabels, ft]);

  // Wired as EditGroup's `onSaveError` prop -- called with the rejected error on a
  // failed save, and with `undefined` at the start of every new submit attempt (see
  // EditGroup.tsx). A real error also resets `edited`, which is what stops
  // EditGroup's own auto-resubmit effect from silently resubmitting the now-reverted
  // (unchanged) form and clearing this error the instant that resubmit starts (issue
  // #1142). Mirrors useEditForm.ts's handleSaveError.
  const handleSaveError = useCallback((error: unknown) => {
    setSaveError(error);
    if (error !== undefined) {
      resetEdited();
    }
  }, [resetEdited]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const saveEdits = useCallback(async (_data: FormData, values: any) => {
    // Acknowledge only the submitted draft; typing during the request must mark
    // the form dirty again so EditGroup saves it after this request finishes.
    resetEdited();
    const result = await updateLog(log.id, values);
    if (result) {
      if (result.log) {
        recordSavedLog?.(result.log);
      }
      if (result.auditLogEdits) {
        recordAuditLogEdits(result.auditLogEdits);
      }
    }
  }, [log, recordAuditLogEdits, recordSavedLog, updateLog, resetEdited]);

  useEffect(() => {
    const load = async () => {
      if (autocompleteLogComment) {
        return;
      }
      const logCommentData = await LogNavigationViewData.loadLogCommentAutocomplete(app.apiClient, log.shipId);
      setAutocompleteLogComment(logCommentData?.values || []);
    };
    load().catch(console.error);
  },
  );

  const courseToWind = useMemo(() => {
    return LogNavigationViewData.courseToWind(log.heading, log.windDirection);
  }, [log.heading, log.windDirection]);

  const courseToWindText = useMemo(() => {
    if (!courseToWind) {
      return "";
    }
    return [
      app.format("angle", courseToWind.angle),
      ft(`point_of_sail_${courseToWind.pointOfSail}`),
      courseToWind.side ? ft(`wind_side_${courseToWind.side}`) : undefined,
    ].filter(Boolean).join(" | ");
  }, [courseToWind]);

  const engineStatuses = useMemo(() => {
    return LogNavigationViewData.engineStatuses(statuses);
  }, [log, statuses]);

  const engines = useMemo(() => {
    return LogNavigationViewData.engines(ship, log, engineStatuses);
  }, [engineStatuses, ship, log]);

  const maxEngine = useMemo(() => {
    return engines.map((e) => e.nmeaNumber || 1).reduce((a, b) => Math.max(a, b), -Infinity);
  }, [engines]);

  const _updateEngine = useCallback(async (engine: string, on: boolean) => {
    await updateLogEngine(log.id, engine, on);
  }, [engines, updateLogEngine, log]);

  const statusData = useMemo(() => {
    return LogNavigationViewData.statusData(status);
  }, [status]);

  // The day view hands over a *closed* window, so a stage this log replaced is in here
  // too. Reefing keeps the sail up, so that stage is dropped: the field shows one chip for
  // the new stage instead of pairing it with a struck one, and -- because the old value no
  // longer counts as taken -- the reef can be shaken back out on this very log (#1288).
  const sailStatuses = useMemo(() => {
    return visibleSailStatuses((statuses || []).filter((s) => s.statusType == IStatusType.sails), log.id);
  }, [statuses, log]);

  const sailName = useCallback((id: string, reef: number, sails: IShipDataSail[]): string => {
    return LogNavigationViewData.sailName(app, id, reef, sails);
  }, []);

  const sailStatusValues = useMemo(() => {
    const sails = Ship.sails(ship);
    return sailStatuses.map((status) => {
      const sail = sails.find((s) => s.id == status.name);
      if (sail && status.name) {
        return {
          icon: status.startLogId == log.id ? Icons.ArrowUp : status.endLogId == log.id ? Icons.ArrowDown : Icons.CircleFilledLarge,
          remove: status.endLogId == undefined || status.startLogId == log.id,
          title: sailName(status.name, status.value, sails),
          value: status.value > 0 ? `${status.name}:${status.value}` : status.name,
        };
      }
      return undefined;
    }).filter(Boolean) as MultiSelectValue[];
  }, [log, statuses, sailStatuses]);

  const sailOptions = useMemo(() => {
    const sails = Ship.sails(ship);
    const sailValues = sailStatuses.map((status) => status.value > 0 ? `${status.name}:${status.value}` : status.name);
    return sails.flatMap((sail) => {
      const items = [{ disabled: sailValues.indexOf(sail.id) >= 0, title: sailName(sail.id, 0, sails), value: sail.id }];
      if (sail.reefNo && sail.reefNo > 0) {
        for (let i = 1; i <= sail.reefNo; i++) {
          items.push({ disabled: sailValues.indexOf(`${sail.id}:${i}`) >= 0, title: sailName(sail.id, i, sails), value: `${sail.id}:${i}` });
        }
      }
      return items;
    });
  }, [ship, log, statuses, sailStatuses]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  const selectSails = useCallback(async (values?: any) => {
    await updateLogSails(log.id, values);
  }, [ship, sailStatuses, updateLogSails, log]);

  function rpmValue(engine: number): string {
    if (!log.engineRpms || log.engineRpms[engine] == undefined || log.engineRpms[engine] < 1) {
      return "";
    }
    return app.format("rpm", log.engineRpms[engine]);
  }

  return (
    <Block className="LogView-navigation" onClick={onClick}>
      <EditGroup buttonFirst={true} changed={edited} editable={formEditable} editableFields={editableFields} error={saveErrorText} isFieldEditable={isFieldEditable} onSave={saveEdits} onSaveError={handleSaveError}>
        <BlockRow>
          <BlockField label={ft("status")} className="block_field_small_12 nav_status">
            <BlockFieldInputs className="input">
              {status && (
                <>
                  <Icon className="ship-status-icon" icon={`status-${IShipStatus[status.value]}`} />
                  {ft("status_" + IShipStatus[status.value])}
                </>
              )}
            </BlockFieldInputs>
          </BlockField>
          <BlockField label={ft("time")} className="block_field_small_6">
            <input defaultValue={app.format("time", log.time)} readOnly={true} />
          </BlockField>
          <BlockField label={ft("time_utc")} className="block_field_small_6">
            <input defaultValue={app.format("timeUtc", log.timeUtc)} readOnly={true} />
          </BlockField>
          <BlockField label={ft("position")} className="block_field_small_14">
            <BlockFieldInputs>
              <input defaultValue={app.format("gps-lat", log.lat)} readOnly={true} />
              <BlockFieldInputsDivider />
              <input defaultValue={app.format("gps-lng", log.lng)} readOnly={true} />
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>
        <BlockRow>
          <BlockField label={ft("course")} className="block_field_small_6">
            <input defaultValue={app.format("course", log.course)} name="course" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.course)} />
          </BlockField>
          <BlockField label={ft("heading")} className="block_field_small_6">
            <input defaultValue={app.format("course", log.heading)} name="heading" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.heading)} />
          </BlockField>
          <BlockField label={ft("sog")} className="block_field_small_6">
            <input defaultValue={app.format("speed", log.sog)} name="sog" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.sog)} />
          </BlockField>
          <BlockField label={ft("stw")} className="block_field_small_6">
            <input defaultValue={app.format("speed", log.stw)} name="stw" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.stw)} />
          </BlockField>
          <BlockField label={ft("distance")} className="block_field_small_6">
            <input defaultValue={app.format("distance", log.distance)} name="distance" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.distance)} />
          </BlockField>
          <BlockField label={ft("log")} className="block_field_small_8">
            <input defaultValue={app.format("distance", log.log)} name="log" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.log)} />
          </BlockField>
          <BlockField label={ft("depth")} className="block_field_small_6">
            <input defaultValue={app.format("meters", log.depth)} name="depth" onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.depth)} />
          </BlockField>
        </BlockRow>
        <BlockRow>
          {Ship.isSailingVessel(ship) && (
            <BlockField label={ft("sails")} className="sail-select">
              <MultiSelect key={sailStatusValues.join("-")} button={<Icon icon={Icons.Plus} />} values={sailStatusValues} onSelect={selectSails} options={sailOptions} readOnly={!formEditable} />
            </BlockField>
          )}
          {Ship.isSailingVessel(ship) && courseToWind && (
            <BlockField label={ft("course_to_wind")} className="course-to-wind block_field_small_14">
              <BlockFieldInputs className="input">{courseToWindText}</BlockFieldInputs>
            </BlockField>
          )}
          <BlockField label={LogNavigationViewData.engineLabel(app, engines)} style={{ maxWidth: LogNavigationViewData.engineFieldMaxWidth(ship.engineCount) }}>
            <BlockFieldInputs>
              {/* Only submit the `engineRpms` array at all when there's something real to
                  round-trip or edit -- either this log already has stored engineRpms data
                  (every unedited slot must resubmit its true value, see below) or an engine
                  is genuinely running right now. Otherwise (a freshly-recorded log with no
                  NMEA/engine data at all, e.g. the log button alone), rendering this hidden
                  length field would force EditGroup's generic array serializer to submit a
                  fabricated engineRpms array (defaulted or empty) for a field whose true
                  stored value is nil -- an unconditional mismatch that gets rejected as a
                  spurious NMEA-protected-field edit, taking the *whole* save down with it
                  (FieldEditTracker's all-or-nothing rejection), even for e.g. a Comment-only
                  edit (issue #1142). */}
              {(log.engineRpms != undefined || engines.some((engine) => engine.on)) && (
                <input type="hidden" name="engineRpms.length" value={maxEngine + 1} />
              )}
              {engines.map((engine, i) => {
                const n = value(-1, engine.nmeaNumber, engine.number);
                const rpm = rpmValue(n);
                let status;
                if (engine.on) {
                  status = (
                    <>
                      {/* off = outline circle, running = filled circle, idle = dot-in-ring,
                          the same three states the log rows draw (#619, #1819). */}
                      <Icon className="LogbookIcon-engine LogbookIcon-engine-nav" icon={engine.idle ? Icons.CircleHalfLarge : Icons.CircleFilledLarge} />
                      <NumberInput defaultValue={rpm} maxLength={5} placeholder={rpm} name={`engineRpms[${n}]`} onChange={onEdited} readOnly={!fieldEditable(ILogEditableField.engineRpms)} className="block_field_rpm" />
                    </>
                  );
                } else {
                  status = (
                    <>
                      <Icon className="LogbookIcon-engine LogbookIcon-engine-nav" icon={Icons.CircleLarge} />
                      {/* No visible input for an off engine, but its true stored rpm (e.g. 0 for
                          a stopped engine, see StatelessNavigation's timeout handling) still needs
                          to be resubmitted whenever the array is being submitted at all --
                          otherwise EditGroup's array serializer falls back to the field's
                          `default: -1` for this missing slot, which the server sees as a spurious
                          edit to the NMEA-protected engineRpms field and rejects the *whole* save
                          (#1142). Only render this once there's real historic data to preserve
                          (log.engineRpms != undefined); with no data at all the field is omitted
                          entirely above. */}
                      {log.engineRpms != undefined && (
                        <input type="hidden" name={`engineRpms[${n}]`} value={log.engineRpms[n] ?? -1} />
                      )}
                    </>
                  );
                }
                return (
                  <React.Fragment key={engine.id ?? i}>
                    {status}
                    {i < ship.engineCount - 1 && <BlockFieldInputsDivider />}
                  </React.Fragment>
                );
              })}
            </BlockFieldInputs>
          </BlockField>
        </BlockRow>
        <BlockRow>
          <BlockField label={ft("comment")} className="">
            <AutocompleteInput defaultValue={log.comment} name="comment" onChange={onEdited} readOnly={!formEditable} suggestions={autocompleteLogComment} />
          </BlockField>
        </BlockRow>
        {status && status.value != IShipStatus.inmotion && status.value != IShipStatus.drifting && (status.startLogId == log.id || (statusData && statusData.location && statusData.location != "")) && (
          <BlockRow>
            <BlockField label={ft("location")}>
              <input defaultValue={statusData?.location} name="location" onChange={onEdited} readOnly={!formEditable || status.startLogId != log.id} />
            </BlockField>
          </BlockRow>
        )}
      </EditGroup>
    </Block>
  );
}
